import type { CustomOptionGroup } from "../../api/products";
import type { ParameterSnapshot, TemplateCreateRequest, TemplateApplyRequest } from "../../api/parameter-set-templates";

export function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(",")}}`;
  return JSON.stringify(value);
}
export async function parameterHash(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical(value)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}
export function snapshotFromGroups(groups: CustomOptionGroup[]): ParameterSnapshot {
  return { schemaVersion: 1, groups: [...groups].filter(group => group.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(group => {
    if (group.config != null && (typeof group.config !== "object" || Array.isArray(group.config))) throw new Error("参数配置格式不兼容，未保存");
    const config = group.config as Record<string, unknown> | null;
    if (Object.keys(config ?? {}).some(key => !["min", "max", "step", "dimensionFields"].includes(key))) throw new Error(`${group.name}包含模板暂不支持的配置，未保存`);
    return { code: group.code, name: group.name, inputType: group.inputType, required: group.required, unit: group.unit,
      config: config == null ? null : structuredClone(config), sortOrder: group.sortOrder,
      values: [...group.values].filter(value => value.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(value => {
        if (value.extraConfig != null) throw new Error(`${group.name}包含模板暂不支持的选项配置，未保存`);
        return { code: value.code, name: value.name, extraConfig: null, imageUrl: value.imageUrl, sortOrder: value.sortOrder };
      }) };
  }) };
}
export function groupsFromSnapshot(snapshot: ParameterSnapshot, productId: string): CustomOptionGroup[] {
  const now = new Date().toISOString();
  return structuredClone(snapshot.groups).map(group => {
    const id = `draft:${crypto.randomUUID()}`;
    return { ...group, id, productId, createdAt: now, updatedAt: now, isActive: true,
      values: group.values.map(value => ({ ...value, id: `draft:${crypto.randomUUID()}`, groupId: id, isActive: true, createdAt: now, updatedAt: now })) };
  });
}
// Only recovery requests are stored here; the template library is stored on the server.
export function pendingRequest<T extends TemplateCreateRequest | TemplateApplyRequest>(kind: "create" | "apply", productId: string) {
  const key = `parameter-set-pending-v1:${kind}:${productId}`;
  return {
    read(): T | null {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as T;
      if (!parsed || typeof parsed.id !== "string" || !/^[a-f0-9-]{36}$/.test(parsed.id)) throw new Error("待核对操作记录异常，请保留当前页面联系管理员");
      return parsed;
    },
    write(value: T) { sessionStorage.setItem(key, JSON.stringify(value)); },
    clear() { sessionStorage.removeItem(key); },
  };
}
